// CSV utilities for Tables import/export. Pure functions — no Firebase, no
// React — so they're unit-testable and reusable (e.g. by a future API export).

// Parse CSV text into { headers, rows }. Handles quoted cells, escaped quotes
// (""), commas and newlines inside quotes, and CRLF line endings. The first
// non-empty line is treated as the header row.
export function parseCsv(text) {
  const rows = [];
  let cur = [];
  let cell = '';
  let inQuotes = false;
  const src = String(text ?? '');

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++; }
        else inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      cur.push(cell); cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      cur.push(cell); cell = '';
      rows.push(cur); cur = [];
    } else {
      cell += ch;
    }
  }
  if (cell !== '' || cur.length) { cur.push(cell); rows.push(cur); }

  // Drop rows that are entirely empty (trailing newlines etc.)
  const nonEmpty = rows.filter(r => r.some(c => String(c).trim() !== ''));
  if (nonEmpty.length === 0) return { headers: [], rows: [] };
  const [headers, ...dataRows] = nonEmpty;
  return { headers: headers.map(h => h.trim()), rows: dataRows };
}

// Serialise headers + rows into CSV text, quoting cells that need it.
export function toCsv(headers, rows) {
  const esc = (v) => {
    const s = String(v ?? '');
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers, ...rows].map(r => r.map(esc).join(',')).join('\n');
}

// Suggest a column→field mapping by case-insensitive name match.
// Returns one entry per CSV header: { header, index, fieldId } where fieldId
// is null when no field matches (caller lets the user pick / create / skip).
export function autoMapColumns(headers, fields) {
  return headers.map((header, index) => {
    const match = fields.find(
      f => String(f.name).trim().toLowerCase() === String(header).trim().toLowerCase()
    );
    return { header, index, fieldId: match ? match.id : null };
  });
}

// Turn parsed rows into record objects using a mapping (entries with a null
// fieldId are skipped). Rows that end up with no values at all are dropped —
// we never create blank records.
export function rowsToRecords(rows, mapping) {
  const active = mapping.filter(m => m.fieldId);
  const records = [];
  for (const row of rows) {
    const values = {};
    for (const m of active) {
      const v = row[m.index];
      if (v !== undefined && String(v).trim() !== '') values[m.fieldId] = String(v).trim();
    }
    if (Object.keys(values).length > 0) records.push({ values });
  }
  return records;
}
